<template>
    <div
        class="card-shadow grid-cols-[240px_minmax(300px,_1fr)_1fr] rounded-xl border border-iron-200 bg-white p-4 transition-all duration-75 ease-out lg:grid"
    >
        <div>
            <p class="font-medium text-iron-700">
                {{ formatDay(reservation.start) }}
            </p>
            <p class="text-sm text-iron-500">
                {{ formatFromTo(reservation.start, reservation.end) }}
            </p>
        </div>
        <div class="mt-4 sm:mt-0">
            <p class="font-medium text-iron-700">
                {{ reservation.event_title }}
            </p>
            <p v-if="reservation.firstname" class="text-iron-500">
                {{ reservation.organizer_name }}
                {{ $t("and") }} {{ reservation.firstname }}
                {{ reservation.lastname ? reservation.lastname : "" }}
            </p>
            <p class="text-iron-500">
                {{ reservation.email }}
            </p>

            <div class="mt-3 flex gap-1.5">
                <div
                    class="inline-flex items-center rounded-md border border-iron-300 px-2 py-1 text-sm font-medium text-iron-600"
                >
                    <Clock9 :size="18" :stroke-width="1.5" class="mr-1" />
                    {{ calcTimeDifference(reservation.start, reservation.end) }}
                </div>

                <div
                    v-if="reservation.stripeConfig"
                    class="inline-flex items-center rounded-md border border-iron-300 px-2 py-1 text-sm font-medium text-iron-600"
                >
                    <CircleDollarSign
                        :size="18"
                        :stroke-width="1.5"
                        class="mr-1"
                    />
                    {{
                        `${
                            reservation.stripeConfig.price / 100
                        } ${reservation.stripeConfig.currency}`
                    }}
                </div>

                <button
                    v-if="reservation.location"
                    class="inline-flex items-center rounded-md border border-iron-300 px-2 py-1 text-sm font-medium text-iron-600"
                    @click="eventTypeClicked"
                >
                    <div
                        class="mr-2 h-4 w-4"
                        v-html="
                            getLocationForId(reservation.location.type)?.icon
                        "
                    ></div>

                    {{
                        clipboard1.copied.value
                            ? $t("copied_to_clipboard")
                            : getLocationForId(reservation.location.type)?.name
                    }}
                </button>

                <button
                    v-if="!reservation.location && reservation.googleMeetLink"
                    class="inline-flex items-center rounded-md border border-iron-300 px-2 py-1 font-medium text-iron-600"
                    @click="eventTypeClicked"
                >
                    <div
                        class="mr-2 h-4 w-4"
                        v-html="getLocationForId('google-meet')?.icon"
                    ></div>

                    {{
                        clipboard1.copied.value
                            ? $t("copied_to_clipboard")
                            : "Google Meet"
                    }}
                </button>
            </div>
        </div>
        <div class="mt-4 xl:mt-0">
            <div class="flex flex-col gap-2 sm:justify-end xl:flex-row">
                <Button
                    v-if="isCancellable"
                    variant="outline"
                    size="sm"
                    @click="cancelEvent"
                >
                    <X :size="20" :stroke-width="1.75" class="mr-2" />
                    {{ $t("bookings.cancel_booking") }}
                </Button>

                <Link v-if="isCancellable" variant="outline" :href="getRoute()">
                    <Button variant="outline" as="span" size="sm">
                        <CalendarClock
                            class="mr-2"
                            :size="20"
                            :stroke-width="1.75"
                        />
                        {{
                            clipboard2.copied.value
                                ? $t("copied_to_clipboard")
                                : $t("bookings.reschedule_link")
                        }}
                    </Button>
                </Link>
            </div>

            <div class="flex h-20 w-full justify-end sm:items-end">
                <DetailsModal :reservation="reservation">
                    <Button variant="outline" size="sm" class="gap-2">
                        <ListCollapse :size="20"></ListCollapse>
                        {{ $t("bookings.detail") }}</Button
                    >
                </DetailsModal>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import axios from "axios";
import route from "ziggy-js";
import { isPast, isThisYear } from "date-fns";
import useConfirm from "../../ConfirmDialog/composables/useConfirm";
import { useClipboard } from "@vueuse/core";
import {
    CalendarClock,
    X,
    Clock9,
    ListCollapse,
    CircleDollarSign,
} from "lucide-vue-next";
import { Button } from "@/components/ui/button";
import { useI18n } from "vue-i18n";
import { useLocalisedTzFormat } from "@/composables/useLocalisedTzFormat";
import { useLocationOptions } from "../../EventTypes/useLocationOptions";
import DetailsModal from "./DetailsModal.vue";
import { Link } from "@inertiajs/vue3";

const { format } = useLocalisedTzFormat();

const { t, locale } = useI18n();
const { locationOptions, getLocationForId } = useLocationOptions();

// !!Copilot generated - possibly wrong
export type Reservation = {
    id: number;
    uuid: string;
    user_id: number;
    event_id: number;
    eventTypeUuid: string;
    integration_event_id: null;
    type: string;
    email: string;
    phone: null;
    firstname: string;
    lastname: string;
    status: string;
    start: string;
    end: string;
    start_date: string;
    end_date: string;
    start_time: string;
    end_time: string;
    seats: number;
    data: null;
    googleMeetLink: string;
    organizer: string;
    organizer_name: string;
    event_title: string;
    customFieldValues: { label: string; value: string }[];
    location: {
        type: string;
        value: string;
    };
    stripeConfig?: {
        enabled: boolean;
        integrationId: 160;
        price: 10000;
        currency: string;
    };
};

const props = defineProps<{
    reservation: Reservation;
}>();

const clipboard1 = useClipboard();
const clipboard2 = useClipboard();
const confirm = useConfirm();

const formatDay = (date: string) => {
    if (locale.value === "cs") {
        return isThisYear(new Date(date))
            ? format(new Date(date), "EEEEEE, dd. MMMM")
            : format(new Date(date), "EEEEEE, dd. MMMM yyyy");
    } else {
        return isThisYear(new Date(date))
            ? format(new Date(date), "EE, d MMM")
            : format(new Date(date), "EE, d MMM yyyy");
    }
};

const formatFromTo = (from: string, to: string) => {
    if (locale.value === "en") {
        return `${format(new Date(from), "h:mm aaa")} - ${format(
            new Date(to),
            "h:mm aaa",
        )}`;
    } else {
        return `${format(new Date(from), "H:mm")} - ${format(
            new Date(to),
            "H:mm",
        )}`;
    }
};

function getRoute() {
    return (
        route("public.event-types.show", {
            eventTypeUuid: props.reservation.eventTypeUuid,
        }) +
        "?reservationUuid=" +
        props.reservation.uuid
    );
}

const cancelEvent = async () => {
    const succes = await confirm.show({
        type: "danger",

        confirm: t("bookings.cancel_booking"),
        cancel: t("cancel"),

        header: t("bookings.cancel_booking"),
        message:
            t("bookings.are_you_sure") +
            "<br>" +
            t("bookings.cancel_description"),
    });

    if (succes) {
        axios
            .post(route("app.reservations.cancel", props.reservation.id))
            .then(() => {
                window.location.reload();
            });
    }
};

const eventType = computed(() => {
    if (props.reservation.type === "call") {
        return t("bookings.video_call");
    }

    if (props.reservation.type === "personal meeting") {
        return t("bookings.personal_meeting");
    }

    return false;
});

const calcTimeDifference = (from: string, to: string) => {
    const fromTime = new Date(from);
    const toTime = new Date(to);

    const diff = toTime.getTime() - fromTime.getTime();

    const minutes = Math.floor(diff / 1000 / 60);

    return `${minutes} min`;
};

const eventTypeClicked = () => {
    if (props.reservation.location && props.reservation.location.value) {
        clipboard1.copy(props.reservation.location.value);
    } else {
        clipboard1.copy(props.reservation.googleMeetLink);
    }
};

const isCancellable = computed(() => {
    if (props.reservation.status === "canceled") {
        return false;
    }

    if (isPast(new Date(props.reservation.start_date))) {
        return false;
    }

    return true;
});

async function copyRescheduleLink() {
    const response = await axios.post(
        route("app.reservations.signed-urls.store", props.reservation.id),
    );
    await clipboard2.copy(response.data.data);
}
</script>

<style scoped>
.card-shadow:hover {
    box-shadow:
        0px 4px 8px -2px rgba(16, 24, 40, 0.1),
        0px 2px 4px -2px rgba(16, 24, 40, 0.06);
}
</style>
